(() => {
  if (window.__rm1MediaInit) return;
  window.__rm1MediaInit = true;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasObserver = 'IntersectionObserver' in window;
  // The extras below are styled only by rm1-theme-fit.css (the rearview-mirror template); other RM-1 templates keep the plain behaviour.
  const themeFit = () => Boolean(document.querySelector('link[href*="rm1-theme-fit"]'));
  // In the theme editor everything stays visible and still, so merchants see the final layout.
  const animate = () => hasObserver && !reduceMotion.matches && !window.Shopify?.designMode && themeFit();
  const ICON_PAUSE = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/></svg>';
  const ICON_PLAY = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M8 5.5v13l11-6.5z"/></svg>';

  /* Looping videos in image slots: play muted while visible, unless the visitor paused them */
  const play = video => {
    if (reduceMotion.matches || video.dataset.rm1Paused) return;
    const attempt = video.play();
    if (attempt) attempt.catch(() => {});
  };
  const videoObserver = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => (entry.isIntersecting ? play(entry.target) : entry.target.pause()));
  }, { rootMargin: '150px 0px' }) : null;

  /* A small pause button, added the first time the video plays */
  const addToggle = video => {
    const host = video.parentElement;
    if (!host || host.querySelector(':scope > .rm1-loop-toggle') || !themeFit()) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rm1-loop-toggle';
    const sync = () => {
      const paused = Boolean(video.dataset.rm1Paused);
      button.innerHTML = paused ? ICON_PLAY : ICON_PAUSE;
      button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    };
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      if (video.dataset.rm1Paused) {
        delete video.dataset.rm1Paused;
        play(video);
      } else {
        video.dataset.rm1Paused = 'true';
        video.pause();
      }
      sync();
    });
    sync();
    host.appendChild(button);
  };

  /* Swipe rows (highlights, gallery on phones): dim the arrow with nothing left to scroll to and move the progress bar.
     The bar reads --rm1-progress and --rm1-thumb from the row's parent. */
  const RAILS = '.rm1-rail[id], .rm1-gl__grid[id]';
  const railState = rail => {
    const range = rail.scrollWidth - rail.clientWidth;
    document.querySelectorAll(`[data-rm1-rail-step][aria-controls="${rail.id}"]`).forEach(button => {
      button.disabled = Number(button.dataset.rm1RailStep) < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= range - 2;
    });
    const host = rail.parentElement;
    if (host && host.querySelector(':scope > .rm1-railnav')) {
      host.style.setProperty('--rm1-progress', range > 0 ? Math.min(1, Math.max(0, rail.scrollLeft / range)).toFixed(3) : '0');
      host.style.setProperty('--rm1-thumb', Math.min(1, rail.clientWidth / Math.max(1, rail.scrollWidth)).toFixed(3));
    }
  };
  const pendingRails = new Set();
  let railFrame = 0;
  const queueRail = rail => {
    pendingRails.add(rail);
    if (railFrame) return;
    railFrame = requestAnimationFrame(() => {
      railFrame = 0;
      pendingRails.forEach(railState);
      pendingRails.clear();
    });
  };
  const allRails = () => document.querySelectorAll(RAILS).forEach(queueRail);

  /* Highlight cards: mark the cards that are fully in view, so captions on cut-off cards can hide */
  const watchCards = rail => {
    if (!hasObserver || !rail.matches('.rm1-rail')) return;
    const cards = new IntersectionObserver(entries => {
      entries.forEach(entry => entry.target.classList.toggle('is-seen', entry.intersectionRatio >= 0.85));
    }, { root: rail, threshold: [0, 0.85, 1] });
    rail.querySelectorAll(':scope > li').forEach(item => cards.observe(item));
    rail.rm1Cards = cards;
    rail.classList.add('is-tracked');
  };

  /* Large image with alert markers: the photo brightens as it comes into view, then the stems flash.
     The flashing stops while the photo is off screen and starts again from the first flash when it returns. */
  const stageObserver = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const stage = entry.target.closest('.rm1-stage');
      if (!stage) return;
      if (entry.intersectionRatio >= 0.3) stage.classList.add('is-shown', 'is-live');
      else if (!entry.isIntersecting) stage.classList.remove('is-live');
    });
  }, { threshold: [0, 0.3] }) : null;
  const armStage = stage => {
    const media = stage.querySelector('.rm1-stage__media');
    if (!media || !media.querySelector('.rm1-marker') || stage.dataset.rm1Armed) return;
    stage.dataset.rm1Armed = 'true';
    stage.classList.add('is-armed');
    stageObserver.observe(media);
  };

  /* Section headings ease into place the first time they scroll into view. Anything already on screen is left alone. */
  const REVEAL = '.rm1-head, .rm1-rail-head, .rm1-stage__text, .rm1-split__text, .rm1-closer__head, .rm1-chooser__intro, .rm1-vs__head';
  const revealObserver = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -12% 0px' }) : null;
  const armReveal = element => {
    if (element.dataset.rm1Reveal) return;
    element.dataset.rm1Reveal = 'true';
    if (element.getBoundingClientRect().top < window.innerHeight) return;
    element.classList.add('rm1-reveal');
    revealObserver.observe(element);
  };

  /* Design stats play once when the row scrolls into view. Unlike the headings this also runs in the theme editor and
     when the row is already on screen, so the effect can be previewed; it ends on the normal layout. */
  const statsObserver = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      statsObserver.unobserve(entry.target);
      requestAnimationFrame(() => entry.target.classList.add('is-in'));
    });
  }, { rootMargin: '0px 0px -10% 0px' }) : null;
  const armStats = row => {
    if (row.dataset.rm1Armed) return;
    row.dataset.rm1Armed = 'true';
    row.classList.add('is-armed');
    statsObserver.observe(row);
  };

  /* Section transitions: where the background changes, the section rises over the one above as a rounded sheet and opens
     to full width as it comes up the screen (rm1-theme-fit.css). The strips beside the sheet keep the colour of the section
     above, which only the page knows, so it is read here into --rm1-under. This also runs in the theme editor. */
  const SHEETS = '.rm1.rm1--edge-rounded, .shopify-section:is(.shopify-section--media-with-text, .shopify-section--rich-text) > .section';
  const fillOf = element => {
    for (let node = element, depth = 0; node && depth < 6; depth += 1) {
      const color = getComputedStyle(node).backgroundColor;
      if (color && !/^(transparent|rgba\(.*,\s*0\))$/.test(color)) return color;
      node = [...node.children].find(child => !/^(STYLE|SCRIPT|LINK|TEMPLATE|NOSCRIPT)$/.test(child.tagName));
    }
    return '';
  };
  const fillAbove = element => {
    let above = element.closest('.shopify-section')?.previousElementSibling;
    while (above && !above.offsetHeight) above = above.previousElementSibling;
    return above ? fillOf(above) : '';
  };
  const channels = color => (color.match(/[\d.]+/g) || []).slice(0, 3).map(Number);
  const nearlySame = (a, b) => {
    const [x, y] = [channels(a), channels(b)];
    return x.length === 3 && y.length === 3 && x.some((v, i) => v !== y[i]) && x.every((v, i) => Math.abs(v - y[i]) <= 24);
  };
  const markSheets = () => {
    if (!themeFit()) return;
    document.querySelectorAll(SHEETS).forEach(sheet => {
      const color = fillAbove(sheet);
      if (color) sheet.style.setProperty('--rm1-under', color);
      else sheet.style.removeProperty('--rm1-under');
    });
    /* A section with a straight top edge on almost the same colour as the one above (black after near-black) fades in
       from that colour, so no line shows between them */
    document.querySelectorAll('.rm1:not(.rm1--edge-rounded, .rm1--edge-glow, .rm1-localnav)').forEach(section => {
      const color = fillAbove(section);
      if (color && nearlySame(color, getComputedStyle(section).backgroundColor)) section.style.setProperty('--rm1-above', color);
      else section.style.removeProperty('--rm1-above');
    });
  };
  const queueSheets = () => requestAnimationFrame(markSheets);

  /* Dark sections: the main photo brightens out of the dark the first time it scrolls into view, as the alerts photo
     does, so sections on the same background flow into each other without a seam. Photos already on screen are left alone. */
  const EMERGE = '.rm1:is(.rm1--dark, .rm1--surface) .rm1-section :is(.rm1-vs__media, .rm1-split__media, .rm1-duo > .rm1-media, .rm1-closer__stage, .rm1-radar__film)';
  const emergeObserver = hasObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting || entry.intersectionRatio < 0.2) return;
      entry.target.classList.add('is-shown');
      emergeObserver.unobserve(entry.target);
    });
  }, { threshold: [0, 0.25] }) : null;
  const armEmerge = media => {
    if (media.dataset.rm1Emerge) return;
    media.dataset.rm1Emerge = 'true';
    if (!media.offsetHeight || media.getBoundingClientRect().top < window.innerHeight) return;
    media.classList.add('rm1-emerge');
    emergeObserver.observe(media);
  };

  function init() {
    markSheets();
    document.querySelectorAll('video.rm1-loop').forEach(video => {
      if (video.dataset.rm1Watched) return;
      video.dataset.rm1Watched = 'true';
      video.muted = true;
      video.playsInline = true;
      video.addEventListener('playing', () => {
        video.classList.add('is-playing');
        addToggle(video);
      });
      if (videoObserver) videoObserver.observe(video);
      else play(video);
    });
    document.querySelectorAll(RAILS).forEach(rail => {
      if (!rail.dataset.rm1Watched) {
        rail.dataset.rm1Watched = 'true';
        rail.addEventListener('scroll', () => queueRail(rail), { passive: true });
        watchCards(rail);
      }
      queueRail(rail);
    });
    if (hasObserver && !reduceMotion.matches && themeFit()) document.querySelectorAll('.rm1 .rm1-stats').forEach(armStats);
    if (animate()) {
      document.querySelectorAll('.rm1 .rm1-stage').forEach(armStage);
      document.querySelectorAll(`.rm1 .rm1-section :is(${REVEAL})`).forEach(armReveal);
      document.querySelectorAll(EMERGE).forEach(armEmerge);
    }
  }

  /* Theme editor: selecting a highlight or gallery block scrolls its row to that card */
  document.addEventListener('shopify:block:select', event => {
    const item = event.target instanceof Element ? event.target.closest('.rm1-rail > li, .rm1-gl__grid > li') : null;
    const rail = item?.parentElement;
    if (!rail || rail.scrollWidth <= rail.clientWidth + 2) return;
    const style = getComputedStyle(rail);
    const inset = parseFloat(style.scrollPaddingLeft) || parseFloat(style.paddingLeft) || 0;
    rail.scrollBy({ left: item.getBoundingClientRect().left - rail.getBoundingClientRect().left - inset, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
  });

  /* Gallery viewer: one photo or video at a time, with previous, next, arrow keys and swipe */
  const viewer = { dialog: null, items: [], index: 0, swipedAt: 0 };
  const show = index => {
    const { dialog, items } = viewer;
    if (!dialog || !items.length) return;
    viewer.index = (index + items.length) % items.length;
    const item = items[viewer.index];
    const stage = dialog.querySelector('[data-rm1-lb-stage]');
    stage.replaceChildren(item.content.cloneNode(true));
    const caption = dialog.querySelector('[data-rm1-lb-cap]');
    if (caption) caption.textContent = item.dataset.caption || '';
    const count = dialog.querySelector('[data-rm1-lb-count]');
    if (count) count.textContent = `${viewer.index + 1} / ${items.length}`;
    const video = stage.querySelector('video');
    if (video) {
      if (item.hasAttribute('data-portrait')) video.dataset.portrait = 'true';
      video.addEventListener('loadedmetadata', () => {
        if (video.videoHeight > video.videoWidth) video.dataset.portrait = 'true';
      }, { once: true });
      video.preload = 'auto';
      const attempt = video.play();
      if (attempt) attempt.catch(() => {});
    }
  };

  /* YouTube and Vimeo in the video window: load the player when the window opens, stop it when it closes.
     The player permissions are set together with the source; an empty frame that asks for them logs console errors. */
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opener = target.closest('[data-rm1-video-open]');
    if (opener) {
      const frame = document.getElementById(opener.dataset.rm1VideoOpen)?.querySelector('iframe[data-src]');
      if (frame && !frame.getAttribute('src')) {
        if (frame.dataset.allow) frame.setAttribute('allow', frame.dataset.allow);
        frame.setAttribute('src', frame.dataset.src);
      }
      return;
    }
    const tile = target.closest('[data-rm1-lb-open]');
    if (tile) {
      const gallery = tile.closest('[data-rm1-lightbox]');
      const dialog = gallery && document.getElementById(gallery.dataset.rm1Lightbox);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      viewer.dialog = dialog;
      viewer.items = [...gallery.querySelectorAll('template[data-rm1-lb-item]')];
      const own = tile.parentElement.querySelector('template[data-rm1-lb-item]');
      dialog.showModal();
      show(Math.max(0, viewer.items.indexOf(own)));
      return;
    }
    const step = target.closest('[data-rm1-lb-step]');
    if (step) return show(viewer.index + Number(step.dataset.rm1LbStep));
    if (target.closest('[data-rm1-lb-close]') || (target.matches('.rm1-lb, .rm1-lb__stage') && Date.now() - viewer.swipedAt > 400)) {
      target.closest('dialog.rm1-lb')?.close();
    }
  });
  document.addEventListener('keydown', event => {
    if (!viewer.dialog?.open || viewer.items.length < 2) return;
    if (event.key === 'ArrowRight') show(viewer.index + 1);
    else if (event.key === 'ArrowLeft') show(viewer.index - 1);
  });
  let swipeX = null;
  document.addEventListener('pointerdown', event => {
    swipeX = event.target instanceof Element && event.target.closest('.rm1-lb__stage') ? event.clientX : null;
  }, { passive: true });
  document.addEventListener('pointerup', event => {
    if (swipeX === null || viewer.items.length < 2) return;
    const distance = event.clientX - swipeX;
    swipeX = null;
    if (Math.abs(distance) > 50) {
      viewer.swipedAt = Date.now();
      show(viewer.index + (distance < 0 ? 1 : -1));
    }
  }, { passive: true });

  document.addEventListener('close', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches('.rm1-dialog')) {
      event.target.querySelectorAll('iframe[data-src]').forEach(frame => {
        frame.removeAttribute('allow');
        frame.removeAttribute('src');
      });
    }
    if (event.target.matches('.rm1-lb')) event.target.querySelector('[data-rm1-lb-stage]')?.replaceChildren();
  }, true);

  /* Theme editor: a section that is removed or reloaded stops being watched, so the observers do not keep its old markup */
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target instanceof Element ? event.target : null;
    if (!root) return;
    root.querySelectorAll('video.rm1-loop').forEach(video => videoObserver?.unobserve(video));
    root.querySelectorAll('.rm1-rail').forEach(rail => rail.rm1Cards?.disconnect());
    root.querySelectorAll(RAILS).forEach(rail => pendingRails.delete(rail));
    root.querySelectorAll('.rm1-stage__media').forEach(media => stageObserver?.unobserve(media));
    root.querySelectorAll('.rm1-reveal').forEach(element => revealObserver?.unobserve(element));
    root.querySelectorAll('.rm1-stats').forEach(row => statsObserver?.unobserve(row));
    root.querySelectorAll('.rm1-emerge').forEach(media => emergeObserver?.unobserve(media));
    if (viewer.dialog && root.contains(viewer.dialog)) Object.assign(viewer, { dialog: null, items: [], index: 0 });
    queueSheets();
  });

  window.addEventListener('beforeprint', () => {
    document.querySelectorAll('.rm1-reveal, .rm1-stats.is-armed').forEach(element => element.classList.add('is-in'));
    document.querySelectorAll('.rm1-emerge').forEach(element => element.classList.add('is-shown'));
  });
  window.addEventListener('resize', allRails, { passive: true });
  window.addEventListener('load', () => {
    allRails();
    markSheets();
  });
  document.addEventListener('shopify:section:reorder', queueSheets);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
